// frontend/src/components/CampaignManager/index.tsx
import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import {
  Box,
  Tabs,
  Tab,
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
  Alert,
  CircularProgress,
  Snackbar,
  useTheme,
  useMediaQuery,
  IconButton,
  Collapse
} from '@mui/material';
import { ArrowClockwise, CaretDown, CaretUp, FileXls } from '@phosphor-icons/react';
import SubPageHeader from '../SubPageHeader';
import { API_CONFIG, authFetch } from '../../config';
import { useCampaignData } from './useCampaignData';

// Import all tabs
import LotteriesTab from './tabs/LotteriesTab';
import CallcentersTab from './tabs/CallcentersTab';
import CampaignsTab from './tabs/CampaignsTab';
import ActionsTab from './tabs/ActionsTab';
import WerbewegeTab from './tabs/WerbewegeTab';
import WerbetraegerTab from './tabs/WerbetraegerTab';
import VermittlerTab from './tabs/VermittlerTab';
import VersandTab from './tabs/VersandTab';

// Import all dialogs
import LotteryDialog from './dialogs/LotteryDialog';
import CallcenterDialog from './dialogs/CallcenterDialog';
import CampaignDetailDialog from './dialogs/CampaignDetailDialog';
import ActionDialog from './dialogs/ActionDialog';
import WerbewegDialog from './dialogs/WerbewegDialog';
import WerbetraegerDialog from './dialogs/WerbetraegerDialog';
import VermittlerDialog from './dialogs/VermittlerDialog';
import VersandDialog from './dialogs/VersandDialog';
import CopyDialog from './dialogs/CopyDialog';
import BulkCopyDialog from './dialogs/BulkCopyDialog';

import { Lottery, Callcenter, Campaign, Action, Werbeweg, Werbetraeger, Vermittler, Versand } from './types';

export default function CampaignManager() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [searchParams] = useSearchParams();

  const [activeTab, setActiveTab] = useState(0);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Tab mapping - Erste Reihe: Kampagnen und Aktionen (0-1), Zweite Reihe: Rest (2-7)
  const tabMap: { [key: string]: number } = {
    'campaigns': 0,
    'actions': 1,
    'lotteries': 2,
    'callcenters': 3,
    'werbewege': 4,
    'werbetraeger': 5,
    'vermittler': 6,
    'versand': 7
  };

  // Set active tab from URL parameter
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && tabMap[tab] !== undefined) {
      setActiveTab(tabMap[tab]);
    }
  }, [searchParams]);

  // Auto-expand advanced section when selecting a tab from it
  useEffect(() => {
    if (activeTab > 1) {
      setShowAdvanced(true);
    }
  }, [activeTab]);

  // Get all data and CRUD functions from the custom hook
  const data = useCampaignData();

  // Dialog states for Lotteries
  const [lotteryDialog, setLotteryDialog] = useState(false);
  const [editingLottery, setEditingLottery] = useState<Lottery | null>(null);

  // Dialog states for Callcenters
  const [callcenterDialog, setCallcenterDialog] = useState(false);
  const [editingCallcenter, setEditingCallcenter] = useState<Callcenter | null>(null);

  // Dialog states for Campaigns
  const [campaignDialog, setCampaignDialog] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<Campaign | null>(null);

  // Dialog states for Actions
  const [actionDialog, setActionDialog] = useState(false);
  const [editingAction, setEditingAction] = useState<Action | null>(null);

  // Dialog states for Werbewege
  const [werbewegDialog, setWerbewegDialog] = useState(false);
  const [editingWerbeweg, setEditingWerbeweg] = useState<Werbeweg | null>(null);

  // Dialog states for Werbeträger
  const [werbetraegerDialog, setWerbetraegerDialog] = useState(false);
  const [editingWerbetraeger, setEditingWerbetraeger] = useState<Werbetraeger | null>(null);

  // Dialog states for Vermittler
  const [vermittlerDialog, setVermittlerDialog] = useState(false);
  const [editingVermittler, setEditingVermittler] = useState<Vermittler | null>(null);

  // Dialog states for Versand
  const [versandDialog, setVersandDialog] = useState(false);
  const [editingVersand, setEditingVersand] = useState<Versand | null>(null);

  // Dialog states for Copy
  const [copyDialog, setCopyDialog] = useState(false);
  const [copyType, setCopyType] = useState<'campaign' | 'action'>('campaign');
  const [copyingItem, setCopyingItem] = useState<Campaign | Action | null>(null);

  // Dialog states for Bulk Copy
  const [bulkCopyDialog, setBulkCopyDialog] = useState(false);
  const [bulkCopyType, setBulkCopyType] = useState<'campaign' | 'action'>('campaign');
  const [bulkCopyIds, setBulkCopyIds] = useState<number[]>([]);
  const [bulkCopySourceLotteryId, setBulkCopySourceLotteryId] = useState<number | undefined>(undefined);

  // Load all data on mount
  useEffect(() => {
    data.loadAll();
  }, []);

  // Loading state
  if (data.loading) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f8f9fa' }}>
        <SubPageHeader title="Kampagnen" />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress />
        </Box>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8f9fa' }}>
      <SubPageHeader title="Kampagnen" />

      <Box sx={{
        maxWidth: 1400,
        margin: isMobile ? '1rem auto' : '2rem auto',
        padding: isMobile ? '0 1rem' : '0 2rem'
      }}>
        {/* Header */}
        <Stack
          direction={isMobile ? 'column' : 'row'}
          justifyContent="space-between"
          alignItems={isMobile ? 'stretch' : 'center'}
          spacing={2}
          sx={{ mb: 3 }}
        >
          <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ color: '#003366', fontWeight: 600 }}>
            Kampagnen-Manager
          </Typography>
          <Button
            variant="outlined"
            startIcon={<ArrowClockwise size={20} />}
            onClick={data.loadAll}
            fullWidth={isMobile}
            sx={{ color: '#003366', borderColor: '#003366' }}
          >
            Aktualisieren
          </Button>
        </Stack>

        {/* Error Alert */}
        {data.error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => data.setError(null)}>
            {data.error}
          </Alert>
        )}

        {/* Workflow & WKZ-Verwaltung Kacheln */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
          gap: isMobile ? '1rem' : '1.5rem',
          mb: isMobile ? '1rem' : '1.5rem'
        }}>
          <Link to="/workflow" style={{ textDecoration: 'none', display: 'block' }}>
            <Card
              sx={{
                boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
                border: '2px solid #dbeafe',
                transition: 'box-shadow 0.2s',
                cursor: 'pointer',
                height: '100%',
                '&:hover': {
                  boxShadow: '0 4px 6px rgba(0,102,204,0.2)'
                }
              }}
            >
              <CardContent sx={{ p: isMobile ? 2 : 3 }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box sx={{ color: '#0066cc' }}>
                    <ArrowClockwise size={isMobile ? 32 : 40} weight="bold" />
                  </Box>
                  <Box>
                    <Typography variant={isMobile ? 'h6' : 'h5'} sx={{ color: '#0066cc', fontWeight: 600, mb: 0.5 }}>
                      Workflow
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#6b7280' }}>
                      Schritt-für-Schritt Anleitung
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Link>

{/* Excel Export Card - only shown when a lottery filter is active */}
          {data.lotteries.length > 0 && (
            <Card
              sx={{
                boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
                border: '2px solid #d1fae5',
                height: '100%',
              }}
            >
              <CardContent sx={{ p: isMobile ? 2 : 3 }}>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                  <Box sx={{ color: '#059669' }}>
                    <FileXls size={isMobile ? 32 : 40} weight="bold" />
                  </Box>
                  <Box>
                    <Typography variant={isMobile ? 'h6' : 'h5'} sx={{ color: '#059669', fontWeight: 600, mb: 0.5 }}>
                      Excel Export
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#6b7280' }}>
                      Daten pro Lotterie exportieren
                    </Typography>
                  </Box>
                </Stack>
                <Stack direction={isMobile ? 'column' : 'row'} spacing={1}>
                  <select
                    id="export-lottery-select"
                    style={{
                      padding: '8px 12px',
                      borderRadius: '4px',
                      border: '1px solid #d1d5db',
                      fontSize: '0.9rem',
                      flex: 1
                    }}
                    defaultValue=""
                  >
                    <option value="" disabled>Lotterie wählen...</option>
                    {data.lotteries.map(l => (
                      <option key={l.id} value={l.id}>{l.name_kurz} - {l.name}</option>
                    ))}
                  </select>
                  <Button
                    variant="contained"
                    size="small"
                    sx={{ bgcolor: '#059669', '&:hover': { bgcolor: '#047857' } }}
                    onClick={async () => {
                      const select = document.getElementById('export-lottery-select') as HTMLSelectElement;
                      if (select?.value) {
                        try {
                          const response = await authFetch(`${API_CONFIG.API_V1}/export/lotteries/${select.value}/campaigns/export`);
                          if (!response.ok) throw new Error('Export failed');
                          const blob = await response.blob();
                          const url = window.URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `campaigns_${select.value}.xlsx`;
                          document.body.appendChild(a);
                          a.click();
                          window.URL.revokeObjectURL(url);
                          document.body.removeChild(a);
                        } catch (error) {
                          console.error('Export error:', error);
                          alert('Fehler beim Export');
                        }
                      }
                    }}
                  >
                    Kampagnen
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    sx={{ bgcolor: '#059669', '&:hover': { bgcolor: '#047857' } }}
                    onClick={async () => {
                      const select = document.getElementById('export-lottery-select') as HTMLSelectElement;
                      if (select?.value) {
                        try {
                          const response = await authFetch(`${API_CONFIG.API_V1}/export/lotteries/${select.value}/actions/export`);
                          if (!response.ok) throw new Error('Export failed');
                          const blob = await response.blob();
                          const url = window.URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `actions_${select.value}.xlsx`;
                          document.body.appendChild(a);
                          a.click();
                          window.URL.revokeObjectURL(url);
                          document.body.removeChild(a);
                        } catch (error) {
                          console.error('Export error:', error);
                          alert('Fehler beim Export');
                        }
                      }
                    }}
                  >
                    Aktionen
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          )}
        </Box>

        {/* Main Card with Tabs */}
        <Card sx={{ boxShadow: '0 1px 3px rgba(0,0,0,0.12)', border: '1px solid #e5e7eb' }}>
          {/* Erste Reihe: Hauptbereiche (Kampagnen & Aktionen) */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center' }}>
            <Tabs
              value={activeTab > 1 ? false : activeTab}
              onChange={(_, v) => setActiveTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{ flex: 1 }}
            >
              <Tab label={isMobile ? `Kamp. (${data.campaigns.length})` : `Kampagnen (${data.campaigns.length})`} />
              <Tab label={isMobile ? `Akt. (${data.actions.length})` : `Aktionen (${data.actions.length})`} />
            </Tabs>
            <Box sx={{ px: 1, borderLeft: 1, borderColor: 'divider' }}>
              <Button
                size="small"
                onClick={() => setShowAdvanced(!showAdvanced)}
                endIcon={showAdvanced ? <CaretUp size={16} /> : <CaretDown size={16} />}
                sx={{ color: '#003366' }}
              >
                {isMobile ? 'Mehr' : 'Erweiterte Verwaltung'}
              </Button>
            </Box>
          </Box>

          {/* Zweite Reihe: Erweiterte Verwaltung (ausklappbar) */}
          <Collapse in={showAdvanced}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: '#f8f9fa' }}>
              <Tabs
                value={activeTab > 1 ? activeTab - 2 : false}
                onChange={(_, v) => setActiveTab(v + 2)}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
              >
                <Tab label={isMobile ? `Lott. (${data.lotteries.length})` : `Lotterien (${data.lotteries.length})`} />
                <Tab label={isMobile ? `CC (${data.callcenters.length})` : `Callcenter (${data.callcenters.length})`} />
                <Tab label={isMobile ? `WW (${data.werbewege.length})` : `Werbewege (${data.werbewege.length})`} />
                <Tab label={isMobile ? `WT (${data.werbetraeger.length})` : `Werbeträger (${data.werbetraeger.length})`} />
                <Tab label={isMobile ? `VM (${data.vermittler.length})` : `Vermittler (${data.vermittler.length})`} />
                <Tab label={isMobile ? `VS (${data.versand.length})` : `Versand (${data.versand.length})`} />
              </Tabs>
            </Box>
          </Collapse>

          <CardContent sx={{ p: isMobile ? 2 : 3 }}>
            {/* Tab 0: Campaigns */}
            {activeTab === 0 && (
              <CampaignsTab
                campaigns={data.campaigns}
                lotteries={data.lotteries}
                deleteCampaign={data.deleteCampaign}
                onOpenDialog={(campaign) => {
                  setEditingCampaign(campaign);
                  setCampaignDialog(true);
                }}
                onOpenCopyDialog={(campaign) => {
                  setCopyingItem(campaign);
                  setCopyType('campaign');
                  setCopyDialog(true);
                }}
                onOpenBulkCopyDialog={(campaignIds, sourceLotteryId) => {
                  setBulkCopyIds(campaignIds);
                  setBulkCopyType('campaign');
                  setBulkCopySourceLotteryId(sourceLotteryId);
                  setBulkCopyDialog(true);
                }}
              />
            )}

            {/* Tab 1: Actions */}
            {activeTab === 1 && (
              <ActionsTab
                actions={data.actions}
                campaigns={data.campaigns}
                lotteries={data.lotteries}
                deleteAction={data.deleteAction}
                onOpenDialog={(action) => {
                  setEditingAction(action);
                  setActionDialog(true);
                }}
                onOpenCopyDialog={(action) => {
                  setCopyingItem(action);
                  setCopyType('action');
                  setCopyDialog(true);
                }}
                onOpenBulkCopyDialog={(actionIds, sourceLotteryId) => {
                  setBulkCopyIds(actionIds);
                  setBulkCopyType('action');
                  setBulkCopySourceLotteryId(sourceLotteryId);
                  setBulkCopyDialog(true);
                }}
              />
            )}

            {/* Tab 2: Lotteries */}
            {activeTab === 2 && (
              <LotteriesTab
                lotteries={data.lotteries}
                campaigns={data.campaigns}
                deleteLottery={data.deleteLottery}
                onOpenDialog={(lottery) => {
                  setEditingLottery(lottery);
                  setLotteryDialog(true);
                }}
              />
            )}

            {/* Tab 3: Callcenters */}
            {activeTab === 3 && (
              <CallcentersTab
                callcenters={data.callcenters}
                deleteCallcenter={data.deleteCallcenter}
                onOpenCallcenterDialog={(callcenter) => {
                  setEditingCallcenter(callcenter);
                  setCallcenterDialog(true);
                }}
              />
            )}

            {/* Tab 4: Werbewege */}
            {activeTab === 4 && (
              <WerbewegeTab
                werbewege={data.werbewege}
                deleteWerbeweg={data.deleteWerbeweg}
                onOpenDialog={(werbeweg) => {
                  setEditingWerbeweg(werbeweg);
                  setWerbewegDialog(true);
                }}
              />
            )}

            {/* Tab 5: Werbeträger */}
            {activeTab === 5 && (
              <WerbetraegerTab
                werbetraeger={data.werbetraeger}
                deleteWerbetraeger={data.deleteWerbetraeger}
                onOpenDialog={(werbetraeger) => {
                  setEditingWerbetraeger(werbetraeger);
                  setWerbetraegerDialog(true);
                }}
              />
            )}

            {/* Tab 6: Vermittler */}
            {activeTab === 6 && (
              <VermittlerTab
                vermittler={data.vermittler}
                deleteVermittler={data.deleteVermittler}
                onOpenDialog={(vermittler) => {
                  setEditingVermittler(vermittler);
                  setVermittlerDialog(true);
                }}
              />
            )}

            {/* Tab 7: Versand */}
            {activeTab === 7 && (
              <VersandTab
                versand={data.versand}
                campaigns={data.campaigns}
                deleteVersand={data.deleteVersand}
                onOpenDialog={(versand) => {
                  setEditingVersand(versand);
                  setVersandDialog(true);
                }}
              />
            )}
          </CardContent>
        </Card>
      </Box>

      {/* All Dialogs */}
      <LotteryDialog
        open={lotteryDialog}
        lottery={editingLottery}
        onClose={() => {
          setLotteryDialog(false);
          setEditingLottery(null);
        }}
        onSave={data.saveLottery}
      />

      <CallcenterDialog
        open={callcenterDialog}
        callcenter={editingCallcenter}
        onClose={() => {
          setCallcenterDialog(false);
          setEditingCallcenter(null);
        }}
        onSave={data.saveCallcenter}
      />

      <CampaignDetailDialog
        open={campaignDialog}
        campaign={editingCampaign}
        lotteries={data.lotteries}
        werbewege={data.werbewege}
        versandList={data.versand}
        onClose={() => {
          setCampaignDialog(false);
          setEditingCampaign(null);
        }}
        onSave={data.saveCampaign}
        onSaveVersand={data.saveVersand}
        onDeleteVersand={data.deleteVersand}
      />

      <ActionDialog
        open={actionDialog}
        action={editingAction}
        campaigns={data.campaigns}
        lotteries={data.lotteries}
        werbewege={data.werbewege}
        werbetraeger={data.werbetraeger}
        vermittler={data.vermittler}
        phoneNumbers={data.phoneNumbers}
        onClose={() => {
          setActionDialog(false);
          setEditingAction(null);
        }}
        onSave={data.saveAction}
        onRefreshWerbewege={data.loadWerbewege}
        onRefreshWerbetraeger={data.loadWerbetraeger}
        onRefreshVermittler={data.loadVermittler}
      />

      <WerbewegDialog
        open={werbewegDialog}
        werbeweg={editingWerbeweg}
        onClose={() => {
          setWerbewegDialog(false);
          setEditingWerbeweg(null);
        }}
        onSave={data.saveWerbeweg}
      />

      <WerbetraegerDialog
        open={werbetraegerDialog}
        werbetraeger={editingWerbetraeger}
        onClose={() => {
          setWerbetraegerDialog(false);
          setEditingWerbetraeger(null);
        }}
        onSave={data.saveWerbetraeger}
      />

      <VermittlerDialog
        open={vermittlerDialog}
        vermittler={editingVermittler}
        onClose={() => {
          setVermittlerDialog(false);
          setEditingVermittler(null);
        }}
        onSave={data.saveVermittler}
      />

      <VersandDialog
        open={versandDialog}
        versand={editingVersand}
        campaigns={data.campaigns}
        lotteries={data.lotteries}
        werbewege={data.werbewege}
        onClose={() => {
          setVersandDialog(false);
          setEditingVersand(null);
        }}
        onSave={data.saveVersand}
      />

      <CopyDialog
        open={copyDialog}
        title={copyType === 'campaign' ? 'Kampagne kopieren' : 'Aktion kopieren'}
        itemName={copyingItem ? (copyType === 'campaign' ? (copyingItem as Campaign).name : `Aktion ${(copyingItem as Action).id}`) : ''}
        currentLotteryId={copyingItem ? (copyType === 'campaign' ? (copyingItem as Campaign).lottery_id : (copyingItem as Action).lotterie_id) : undefined}
        lotteries={data.lotteries}
        onClose={() => {
          setCopyDialog(false);
          setCopyingItem(null);
        }}
        onCopy={async (targetLotteryId) => {
          if (!copyingItem) return;
          if (copyType === 'campaign') {
            await data.copyCampaign(copyingItem.id, targetLotteryId);
          } else {
            await data.copyAction(copyingItem.id, targetLotteryId);
          }
        }}
      />

      <BulkCopyDialog
        open={bulkCopyDialog}
        title={bulkCopyType === 'campaign' ? 'Kampagnen kopieren' : 'Aktionen kopieren'}
        itemType={bulkCopyType}
        selectedCount={bulkCopyIds.length}
        currentLotteryId={bulkCopySourceLotteryId}
        lotteries={data.lotteries}
        onClose={() => {
          setBulkCopyDialog(false);
          setBulkCopyIds([]);
          setBulkCopySourceLotteryId(undefined);
        }}
        onCopy={async (targetLotteryId, copyActions) => {
          if (bulkCopyType === 'campaign') {
            await data.bulkCopyCampaigns(bulkCopyIds, targetLotteryId, copyActions);
          } else {
            await data.bulkCopyActions(bulkCopyIds, targetLotteryId);
          }
        }}
      />

      {/* Success Snackbar */}
      <Snackbar
        open={!!data.successMessage}
        autoHideDuration={6000}
        onClose={() => data.setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => data.setSuccessMessage(null)} severity="success" sx={{ width: '100%' }}>
          {data.successMessage}
        </Alert>
      </Snackbar>
    </div>
  );
}
